// src/context/TestContext.tsx
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  getTests,
  getTest,
  createTest,
  updateTest,
  deleteTest,
  publishTest,
  unpublishTest,
} from '../api/tests';
import {
  createQuestion,
  duplicateQuestion,
  deleteQuestion,
} from '../api/questions';
import { Test, Question } from '../types/testData';

interface TestContextType {
  tests: Test[];
  currentTest: Test | null;
  loading: boolean;
  error: string | null;

  setCurrentTest: (test: Test | null) => void;
  fetchTests: () => Promise<void>;
  fetchTest: (testId: number) => Promise<void>;

  addTest: (testData: { title: string; description: string; shuffle_ranges?: [number, number][] | null; color_mode?: boolean; analysis_algorithm?: string | null; color_labels?: string[] | null; color_pair_index?: number[][] | null; methodology?: string | null }) => Promise<Test | null>;
  updateTest: (testId: number, updates: Partial<Test>) => Promise<void>;
  deleteTest: (testId: number) => Promise<void>;
  publishTest: (testId: number) => Promise<void>;
  unpublishTest: (testId: number) => Promise<void>;

  addQuestion: (testId: number, question: Partial<Question>) => Promise<void>;
  deleteQuestion: (testId: number, questionId: number) => Promise<void>;
  duplicateQuestion: (testId: number, questionId: number, count?: number) => Promise<void>;
}

const TestContext = createContext<TestContextType | undefined>(undefined);

const errMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

export const TestProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tests, setTests] = useState<Test[]>([]);
  const [currentTest, setCurrentTest] = useState<Test | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTests();
      setTests(data);
    } catch (err: unknown) {
      setError(errMessage(err, 'Не удалось загрузить тесты'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTest = useCallback(async (testId: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTest(testId);
      setCurrentTest(data);
    } catch (err: unknown) {
      setError(errMessage(err, 'Не удалось загрузить тест'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const addTest = async (testData: { title: string; description: string; shuffle_ranges?: [number, number][] | null; color_mode?: boolean; analysis_algorithm?: string | null; color_labels?: string[] | null; color_pair_index?: number[][] | null; methodology?: string | null }) => {
    try {
      const newTest = await createTest(testData);
      setTests((prev) => [...prev, newTest]);
      return newTest;
    } catch (err: unknown) {
      setError(errMessage(err, 'Ошибка создания теста'));
      console.error(err);
      return null;
    }
  };

  const updateTestFn = async (testId: number, updates: Partial<Test>) => {
    try {
      await updateTest(testId, updates);
      setTests((prev) =>
        prev.map((t) => (t.id === testId ? { ...t, ...updates } : t))
      );
      if (currentTest?.id === testId) {
        setCurrentTest((prev) => (prev ? { ...prev, ...updates } : null));
      }
    } catch (err: unknown) {
      setError(errMessage(err, 'Ошибка обновления теста'));
      console.error(err);
    }
  };

  const deleteTestFn = async (testId: number) => {
    try {
      await deleteTest(testId);
      setTests((prev) => prev.filter((t) => t.id !== testId));
      if (currentTest?.id === testId) {
        setCurrentTest(null);
      }
    } catch (err: unknown) {
      setError(errMessage(err, 'Ошибка удаления теста'));
      console.error(err);
    }
  };

  const publishTestFn = async (testId: number) => {
    try {
      await publishTest(testId);
      setTests((prev) =>
        prev.map((t) => (t.id === testId ? { ...t, is_published: true } : t))
      );
      if (currentTest?.id === testId) {
        setCurrentTest((prev) => (prev ? { ...prev, is_published: true } : null));
      }
    } catch (err: unknown) {
      setError(errMessage(err, 'Ошибка публикации теста'));
      console.error(err);
    }
  };

  const unpublishTestFn = async (testId: number) => {
    try {
      await unpublishTest(testId);
      setTests((prev) =>
        prev.map((t) => (t.id === testId ? { ...t, is_published: false } : t))
      );
      if (currentTest?.id === testId) {
        setCurrentTest((prev) => (prev ? { ...prev, is_published: false } : null));
      }
    } catch (err: unknown) {
      setError(errMessage(err, 'Ошибка снятия публикации'));
      console.error(err);
    }
  };

  const addQuestion = async (testId: number, question: Partial<Question>) => {
    try {
      await createQuestion(testId, question);
      await fetchTest(testId);
    } catch (err: unknown) {
      setError(errMessage(err, 'Ошибка добавления стимула'));
      console.error(err);
    }
  };

  const deleteQuestionFn = async (testId: number, questionId: number) => {
    try {
      await deleteQuestion(questionId);
      await fetchTest(testId);
    } catch (err: unknown) {
      setError(errMessage(err, 'Ошибка удаления стимула'));
      console.error(err);
    }
  };

  const duplicateQuestionFn = async (testId: number, questionId: number, count: number = 50) => {
    try {
      await duplicateQuestion(questionId, count);
      await fetchTest(testId);
    } catch (err: unknown) {
      setError(errMessage(err, 'Ошибка дублирования стимула'));
      console.error(err);
    }
  };

  const value: TestContextType = {
    tests,
    currentTest,
    loading,
    error,
    setCurrentTest,
    fetchTests,
    fetchTest,
    addTest,
    updateTest: updateTestFn,
    deleteTest: deleteTestFn,
    publishTest: publishTestFn,
    unpublishTest: unpublishTestFn,
    addQuestion,
    deleteQuestion: deleteQuestionFn,
    duplicateQuestion: duplicateQuestionFn,
  };

  return <TestContext.Provider value={value}>{children}</TestContext.Provider>;
};

export const useTestContext = () => {
  const context = useContext(TestContext);
  if (!context) {
    throw new Error('useTestContext must be used within TestProvider');
  }
  return context;
};
